import { z } from 'zod';
import { Basis, CheckId, DesignationStatus, EngagementAreaKey, ExclusivityGroup, FamilyKey, Segment, SizeOnKey, SpecGroup, SpecStatus, SubFloorKey, } from './domain.js';
import { CentsSchema } from './money.js';
import { BpBand, PromoCatalogue } from './promotions.js';
/**
 * The compiled ruleset — the engine's only reference input.
 *
 * The database stores this data normalised across ~25 versioned tables so the
 * admin CRUD UI gets real foreign keys and constraints. On publish, those tables
 * are compiled into one immutable JSONB artifact in this shape and hashed. The
 * engine reads only the artifact: published artifacts never change, so the
 * in-memory cache needs no invalidation protocol, evaluation costs zero queries,
 * and `(rulesetHash, engineVersion, inputHash)` reproduces any past result
 * exactly. See the plan, Part 3.
 */
/**
 * One band of a tier table.
 *
 * `ceiling` is display-only — band selection takes the LAST band whose floor is
 * met and never tests the ceiling, so a deal above the top band still pays the
 * top band. Correctness therefore depends on bands ascending by floor, which the
 * database enforces with an EXCLUDE constraint and a constraint trigger.
 *
 * `marketC` is null on every Azure table; the payout falls back to `marketB`.
 */
export const TierBand = z
    .object({
    size: z.string().min(1),
    /** Inclusive minimum, in cents for money bases and whole units for seats/MPU. */
    floor: z.number().int().min(0),
    ceiling: z.number().int().min(0).nullable(),
    marketA: CentsSchema,
    marketB: CentsSchema,
    marketC: CentsSchema.nullable(),
    /** Guide minimum delivery hours. Drives the $/hr figure; null where unpublished. */
    minHours: z.number().int().positive().nullable(),
})
    .strict();
export const TierSet = z
    .object({
    key: z.string().min(1),
    label: z.string().min(1),
    /**
     * `shared` sets are cited by several engagements and editing one changes all
     * of them. `private` sets belong to a single engagement — including the three
     * pairs whose bands happen to be byte-identical, because coincidental
     * equality is not sharing.
     */
    scope: z.enum(['shared', 'private']),
    bands: z.array(TierBand).min(1),
})
    .strict()
    .refine((s) => s.bands.every((b, i) => i === 0 || b.floor > (s.bands[i - 1]?.floor ?? -1)), 'Tier bands must ascend by floor');
/**
 * One term of the partner-capability gate. Terms are OR-ed by default: holding
 * any one of them satisfies the gate.
 *
 * The legacy code let `minSpecCount` *overwrite* the accumulated result rather
 * than OR with it — latent, since no engagement combined it with another term.
 * We OR consistently and expose `capabilityCombinator` on the engagement so the
 * other reading stays expressible as data. Recorded in DIVERGENCES.md.
 */
export const CapabilityTerm = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('any_spec'), specKey: z.string().min(1) }).strict(),
    z.object({ kind: z.literal('any_spec_group'), group: SpecGroup }).strict(),
    z.object({ kind: z.literal('any_designation'), designationKey: z.string().min(1) }).strict(),
    z.object({ kind: z.literal('min_spec_count'), group: SpecGroup, n: z.number().int().positive() }).strict(),
]);
/**
 * A qualifying-service floor. BOTH tests must pass: an absolute minimum for the
 * selected band, and a percentage of WHOLE-PROJECT incremental ACR — not of the
 * engagement's own family total. That denominator is easy to get wrong and is
 * the reason this is modelled explicitly rather than inline.
 */
export const SubFloor = z
    .object({
    key: SubFloorKey,
    label: z.string().min(1),
    pct: z.number().positive().max(100),
    /** Band size → absolute minimum in USD cents. Must cover every band exactly. */
    mins: z.record(z.string(), CentsSchema),
})
    .strict();
/**
 * `fams` is any-of: at least one family must carry incremental spend.
 * `famsAll` is all-of, where each group is itself an or-list — the deal must have
 * spend on at least one family in every group. The only live example is
 * `['mdc', 'sentinel']` as a single group on the two MDC/Sentinel Core variants.
 */
export const FamilyRequirementGroup = z.array(FamilyKey).min(1);
export const Engagement = z
    .object({
    key: z.string().min(1),
    name: z.string().min(1),
    area: EngagementAreaKey,
    phase: z.string().min(1),
    basis: Basis,
    tierSetKey: z.string().min(1),
    segments: z.array(Segment).min(1),
    /** Null means the engagement stacks rather than competing. */
    exclusivityGroup: ExclusivityGroup.nullable(),
    /** Sizes on this single service's ACR instead of on `fams`. */
    sizeOn: SizeOnKey.nullable(),
    fams: z.array(FamilyKey),
    famsAll: z.array(FamilyRequirementGroup),
    capabilityTerms: z.array(CapabilityTerm),
    capabilityCombinator: z.enum(['any', 'all']).default('any'),
    checks: z.array(CheckId),
    subFloor: SubFloor.nullable(),
    /** Band size → conditional second payment. Displayed, never added to a total. */
    stageTwo: z.record(z.string(), CentsSchema).nullable(),
    /** Must be claimable at the SAME band size for this bonus to be claimable. */
    prereqKey: z.string().min(1).nullable(),
    /** Blocked until a paired ISV is confirmed; the tool cannot verify it. */
    requiresPair: z.boolean(),
    workloads: z.string(),
    duration: z.string().nullable(),
    seatLabel: z.string().nullable(),
    acvLabel: z.string().nullable(),
    mpuLabel: z.string().nullable(),
    poe: z.array(z.string()),
    notes: z.array(z.string()),
    custom: z.array(z.string()),
    sort: z.number().int().min(0),
})
    .strict();
export const Specialization = z
    .object({ key: z.string().min(1), name: z.string().min(1), group: SpecGroup })
    .strict();
export const Designation = z.object({ key: z.string().min(1), name: z.string().min(1) }).strict();
/**
 * A workload category the user can assign a row to.
 *
 * `fam` null means supporting spend (networking, backup, other): it counts toward
 * incremental ACR but belongs to no engagement, and is only folded into a band
 * when the user opts in, pro-rata.
 *
 * `isFallback` replaces the legacy's load-bearing "unknown keys resolve to array
 * index 12". Exactly one category carries it.
 */
export const WorkloadCategory = z
    .object({
    key: z.string().min(1),
    label: z.string().min(1),
    fam: FamilyKey.nullable(),
    sub: SubFloorKey.nullable(),
    isBaseline: z.boolean(),
    isFallback: z.boolean(),
    colour: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    sort: z.number().int().min(0),
})
    .strict();
export const Ruleset = z
    .object({
    version: z.string().min(1),
    label: z.string().min(1),
    /**
     * The financial year the guide covers, e.g. "1 Jul 2026 – 30 Jun 2027".
     * Display metadata only — no gate reads it. Shown so a consultant always
     * knows which term produced the numbers in front of them.
     */
    term: z.string().min(1),
    engineMin: z.string().min(1),
    engagements: z.array(Engagement).min(1),
    tierSets: z.array(TierSet).min(1),
    specializations: z.array(Specialization).min(1),
    designations: z.array(Designation).min(1),
    workloadCategories: z.array(WorkloadCategory).min(1),
    familyLabels: z.record(FamilyKey, z.string()),
    defaultSpecStatuses: z.record(z.string(), SpecStatus),
    defaultDesignationStatuses: z.record(z.string(), DesignationStatus),
    defaultWorkloads: z.array(z.object({ name: z.string(), category: z.string() }).strict()),
    /** CSP promotions — the customer-discount catalogue. Safe to serve. */
    promoCatalogue: PromoCatalogue,
    /**
     * Business Processes CSP bands, used to price the band-drop risk a discount
     * creates. **Contains MCI payouts — never serve these to the browser.**
     * Ordered ascending by floor.
     */
    bpBands: z.array(BpBand).min(1),
})
    .strict();
//# sourceMappingURL=ruleset.js.map